import React, { useState } from 'react';
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Scatter, XAxis, YAxis } from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate, formatDuration, formatPace, SOURCE_LABELS } from '../utils/format';
import { axisFor, Y_AXIS } from '../utils/axis';
import {
  ChartPoint,
  DistanceSeries,
  domainOf,
  formatMetricValue,
  ProgressMetric,
  ProgressView,
  timeAxisTicks,
} from '../utils/progression';

const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 12, right: 16, bottom: 0, left: 0 };
/** What the other distances keep while one is highlighted: dimmed, still readable as context. */
const DIM_LINE = 0.18;
const DIM_DOT = 0.28;
/** Invisible hit target around each dot, larger than the mark (touch friendly). */
const HIT_RADIUS = 12;

interface DotProps {
  cx?: number;
  cy?: number;
  payload?: ChartPoint;
}

interface Props {
  series: DistanceSeries[];
  view: ProgressView;
  metric: ProgressMetric;
  /** The distance the user pinned (legend click). Others fade but stay visible. */
  focus: string | null;
  /** The distance under the pointer (legend or dot). It overrides the pinned one while it lasts. */
  hover: string | null;
  onHover: (code: string | null) => void;
}

/**
 * The progression chart: time on x, pace or finishing time on y (reversed, so faster is up), one line per distance.
 * Hover, focus or tap a dot to read it below the chart; click or press Enter to open its activity.
 */
const RecordsProgressChart: React.FC<Props> = ({ series, view, metric, focus, hover, onHover }) => {
  const navigate = useNavigate();
  const [active, setActive] = useState<ChartPoint | null>(null);
  const domain = domainOf(series);

  if (!domain) {
    return null;
  }

  const highlight = hover ?? focus;
  const highlighted = series.find(s => s.code === highlight) ?? null;
  const dim = (code: string) => highlighted !== null && highlighted.code !== code;
  // The highlighted distance is drawn last, so it sits on top of the faded ones.
  const ordered = highlighted ? [...series.filter(s => s !== highlighted), highlighted] : series;
  const lastDot = new Map(series.map(s => [s.code, s.dots[s.dots.length - 1]]));

  const yValues = series.flatMap(s => [
    ...s.dots.map(p => p.y),
    ...s.line.map(p => p.y),
    // The trend only counts for the highlighted distance, so it never stretches the axis for the others.
    ...(s === highlighted && s.trend ? [s.trend.y1, s.trend.y2] : []),
    ...(metric === 'gap' ? [0] : []),
  ]);
  const rawAxis = axisFor(yValues, metric === 'gap' ? 'number' : 'time', 8);
  // A gap below 0 cannot exist (the PB is the best), so the axis starts at 0 even if a trend line runs under it.
  const yAxis = rawAxis && metric === 'gap' ? clampFromZero(rawAxis) : rawAxis;
  const xAxis = timeAxisTicks(domain.x[0], domain.x[1]);
  const formatY = (value: number) => formatMetricValue(metric, value, formatDuration);

  const openActivity = (point: ChartPoint) => {
    if (point.effort.activityId != null) navigate(`/activities/${point.effort.activityId}`);
  };

  const renderDot = (props: DotProps) => {
    const { cx, cy, payload } = props;
    if (cx === undefined || cy === undefined || !payload) return <g />;
    const { effort } = payload;
    const linked = effort.activityId != null;
    const faded = dim(payload.code);
    const isBest = effort.timeS === series.find(s => s.code === payload.code)?.bestTimeS;
    const isLast = lastDot.get(payload.code) === payload;
    const label = [
      payload.name,
      formatDate(effort.date),
      effort.activityName ?? 'Manual time',
      formatDuration(effort.timeS),
      formatPace(effort.paceSPerKm),
      SOURCE_LABELS[effort.source],
    ].join(', ');
    return (
      <g
        role={linked ? 'link' : 'img'}
        aria-label={label}
        tabIndex={0}
        style={{ cursor: linked ? 'pointer' : 'default', outline: 'none' }}
        opacity={faded ? DIM_DOT : 1}
        onMouseEnter={() => {
          setActive(payload);
          onHover(payload.code);
        }}
        onFocus={() => {
          setActive(payload);
          onHover(payload.code);
        }}
        onBlur={() => onHover(null)}
        onTouchStart={() => {
          setActive(payload);
          onHover(payload.code);
        }}
        onClick={() => {
          setActive(payload);
          openActivity(payload);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') openActivity(payload);
        }}
      >
        <circle cx={cx} cy={cy} r={HIT_RADIUS} fill="transparent" />
        {isBest && <circle cx={cx} cy={cy} r={8} fill="none" stroke={payload.style.color} strokeWidth={2} />}
        <circle
          cx={cx}
          cy={cy}
          r={highlight === payload.code ? 5 : 4}
          fill={payload.style.color}
          stroke="var(--surface-1)"
          strokeWidth={2}
        />
        {isLast && highlight === payload.code && (
          <text
            x={cx - 10}
            y={cy - 12}
            textAnchor="end"
            fontSize={12}
            fontWeight={600}
            fill="var(--text-primary)"
            stroke="var(--surface-1)"
            strokeWidth={3}
            paintOrder="stroke"
          >
            {payload.name} {formatY(payload.y)}
          </text>
        )}
      </g>
    );
  };

  return (
    <>
      <div className="progress-chart" data-testid="progress-chart" onMouseLeave={() => onHover(null)}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart margin={MARGIN}>
            <CartesianGrid stroke="var(--chart-grid)" />
            <XAxis
              type="number"
              dataKey="x"
              domain={domain.x}
              ticks={xAxis.ticks}
              tickFormatter={xAxis.format}
              tick={AXIS}
              tickMargin={4}
            />
            <YAxis
              {...Y_AXIS}
              type="number"
              dataKey="y"
              domain={yAxis ? yAxis.domain : domain.y}
              ticks={yAxis?.ticks}
              reversed
              tickFormatter={formatY}
              width={metric === 'gap' ? 52 : 64}
              interval={0}
            />
            {ordered.map(s => (
              <Line
                key={`line-${s.code}`}
                data={s.line}
                dataKey="y"
                name={s.name}
                stroke={s.style.color}
                strokeWidth={highlight === s.code ? 4 : 3}
                strokeOpacity={dim(s.code) ? DIM_LINE : 1}
                strokeDasharray={s.style.dashed ? '6 4' : undefined}
                type={view === 'pb' ? 'stepAfter' : 'linear'}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
              />
            ))}
            {highlighted?.trend && (
              <Line
                key="trend"
                data={[
                  { x: highlighted.trend.x1, y: highlighted.trend.y1 },
                  { x: highlighted.trend.x2, y: highlighted.trend.y2 },
                ]}
                dataKey="y"
                stroke={highlighted.style.color}
                strokeWidth={2}
                strokeDasharray="2 5"
                strokeLinecap="round"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
                name="Trend"
              />
            )}
            {ordered.map(s => (
              <Scatter
                key={`dots-${s.code}`}
                data={s.dots}
                dataKey="y"
                name={s.name}
                fill={s.style.color}
                shape={(props: unknown) => renderDot(props as DotProps)}
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="progress-readout" aria-live="polite">
        {active ? (
          <>
            <span className="progress-swatch" style={{ background: active.style.color }} aria-hidden="true" />
            <strong>{active.name}</strong>
            {' · '}
            {formatDate(active.effort.date)}
            {' · '}
            {active.effort.activityName ?? 'Manual time'}
            {' · '}
            <span className="tabular">{formatDuration(active.effort.timeS)}</span>
            {' · '}
            <span className="tabular">{formatPace(active.effort.paceSPerKm)}</span>
            {' · '}
            {SOURCE_LABELS[active.effort.source]}
            {active.effort.activityId != null && (
              <>
                {' · '}
                <Link to={`/activities/${active.effort.activityId}`}>Open activity</Link>
              </>
            )}
          </>
        ) : (
          <span className="muted">Hover, focus or tap a point for details. Click a point to open its activity.</span>
        )}
      </div>
      <p className="muted small mb-0 mt-1">
        {metric === 'pace' ? 'Pace per km' : metric === 'gap' ? 'Gap to each distance’s personal best' : 'Finishing time'} on
        the vertical axis, faster is higher. Ringed dot: the personal best. Thick line:{' '}
        {view === 'pb' ? 'the all-time personal-best sequence' : 'the yearly best'}.
        {highlighted?.trend && <> Dotted line: trend, {trendText(highlighted, metric)}.</>}
      </p>
    </>
  );
};

/** Drops the ticks below zero from a gap axis. */
function clampFromZero(axis: { domain: [number, number]; ticks: number[] }) {
  const ticks = axis.ticks.filter(t => t >= 0);
  return { domain: [ticks[0], ticks[ticks.length - 1]] as [number, number], ticks };
}

/** "4 s/km faster per year", from the sign and size of the trend slope. */
function trendText(series: DistanceSeries, metric: ProgressMetric): string {
  const perYear = series.trend?.perYear ?? 0;
  const better = perYear < 0;
  const size = Math.abs(perYear);
  const amount =
    metric === 'gap' ? `${size.toFixed(1)} points` : metric === 'pace' ? `${Math.round(size)} s/km` : formatDuration(size);
  return `${amount} ${better ? 'faster' : 'slower'} per year`;
}

export default RecordsProgressChart;
